import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { createClient } from "@supabase/supabase-js";
import guideCatalog from "./generated/guides.json";
import "./styles.css";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || "";
const APP_ORIGIN = "https://app.layover.tips";
const ADMIN_EMAILS = new Set(["charles@cognitiveforest.io", "eklayovertips@gmail.com"]);
const LOGO_URL = "https://layover.tips/assets/images/layover-logo-mark.svg";
const HERO_URL = "https://media.layover.tips/site/home/briefing-room-hero-2026-05-20.jpg";

const supabase = SUPABASE_URL && SUPABASE_KEY
  ? createClient(SUPABASE_URL, SUPABASE_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true
      }
    })
  : null;

function currentRoute() {
  return `${window.location.pathname}${window.location.search}`;
}

function navigate(href, { replace = false } = {}) {
  if (replace) {
    window.history.replaceState({}, "", href);
  } else {
    window.history.pushState({}, "", href);
  }
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function InternalLink({ href, children, className, ...props }) {
  return (
    <a
      href={href}
      className={className}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        navigate(href);
      }}
      {...props}
    >
      {children}
    </a>
  );
}

function useAuth() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(Boolean(supabase));

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session || null);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setLoading(false);
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  return {
    configured: Boolean(supabase),
    loading,
    session
  };
}

function App() {
  const [route, setRoute] = useState(currentRoute());
  const auth = useAuth();
  const url = new URL(route, window.location.origin);
  const path = url.pathname.replace(/\/+$/, "") || "/";

  useEffect(() => {
    const onPopState = () => setRoute(currentRoute());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    const isGuideRoute = path === "/guides" || path.startsWith("/guides/");
    document.title = isGuideRoute ? "App Links · The Layover" : "The Layover App";

    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    robots.content = path === "/" ? "index,follow" : "noindex,nofollow";

    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor) themeColor.content = path === "/" ? "#0d1622" : "#480f1a";
  }, [path]);

  if (path === "/") return <LandingPage />;
  if (path === "/signin") return <SigninPage auth={auth} />;
  if (path === "/auth/callback") return <AuthCallbackPage auth={auth} />;
  if (path === "/admin") return <AdminPage auth={auth} />;
  if (path === "/guides") return <GuideDirectoryPage />;
  if (path.startsWith("/guides/")) {
    const slug = decodeURIComponent(path.slice("/guides/".length));
    return <GuideLinkPage slug={slug} />;
  }
  if (path === "/privacy") return <PolicyPage type="privacy" />;
  if (path === "/terms") return <PolicyPage type="terms" />;
  if (path === "/support") return <SupportPage />;
  if (path === "/delete-account") return <DeleteAccountPage />;
  return <NotFoundPage />;
}

function BrandHeader() {
  return (
    <>
      <div className="top-stripe">Insider guides. Better connections.</div>
      <header className="site-header">
        <InternalLink className="brand" href="/" aria-label="The Layover app home">
          <img src={LOGO_URL} alt="" width="58" height="37" />
          <span className="brand-copy">
            The Layover
            <small>App for crew</small>
          </span>
        </InternalLink>
        <nav aria-label="App website navigation">
          <a href="https://layover.tips/">Visit The Layover</a>
        </nav>
      </header>
    </>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="footer-kicker">Est. 2012</div>
      <p className="footer-brandline">The Layover · Made For Crew, By Crew</p>
      <nav aria-label="App policy links">
        <InternalLink href="/terms">Terms</InternalLink>
        <InternalLink href="/privacy">Privacy</InternalLink>
        <InternalLink href="/support">Support</InternalLink>
      </nav>
      <p>Independent publisher. Not affiliated with or endorsed by any airline.</p>
      <p>This is a preparatory app surface. Current memberships and guides remain at layover.tips.</p>
    </footer>
  );
}

function PublicLayout({ children }) {
  return (
    <div className="page-shell">
      <BrandHeader />
      {children}
      <SiteFooter />
    </div>
  );
}

function LandingPage() {
  return (
    <div className="landing-page">
      <BrandHeader />
      <main>
        <section className="app-hero" style={{ "--hero-image": `url(${HERO_URL})` }}>
          <div className="hero-content">
            <p className="eyebrow">The Layover · Native apps coming soon</p>
            <h1>Don&apos;t stay in the hotel.</h1>
            <p className="hero-copy">
              We&apos;re building The Layover for iPhone and Android: the same crew-first city guidance,
              shaped for the phone already in your pocket.
            </p>
            <div className="button-row">
              <a className="button button-primary" href="https://layover.tips/">Visit The Layover</a>
              <a className="button button-primary" href="https://layover.tips/sign-up">Sign Up</a>
            </div>
          </div>
        </section>

      </main>
      <SiteFooter />
    </div>
  );
}

function SigninPage({ auth }) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!auth.loading && auth.session) navigate("/admin", { replace: true });
  }, [auth.loading, auth.session]);

  async function submit(event) {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    setMessage("");

    if (!supabase) {
      setMessage("Administrator sign-in is not configured in this environment.");
      return;
    }

    if (!ADMIN_EMAILS.has(normalizedEmail)) {
      setMessage("This sign-in is reserved for authorized Layover administrators.");
      return;
    }

    setBusy(true);
    const { error } = await supabase.auth.signInWithOtp({
      email: normalizedEmail,
      options: {
        shouldCreateUser: false,
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    setBusy(false);
    setMessage(error ? error.message : "Check your email for a secure sign-in link.");
  }

  return (
    <PublicLayout>
      <main className="narrow-page">
        <section className="panel auth-panel" aria-labelledby="signin-title">
          <p className="eyebrow dark">Private prototype</p>
          <h1 id="signin-title">Administrator sign in</h1>
          <p>Authorized Layover administrators can receive a secure, passwordless sign-in link.</p>
          <form onSubmit={submit} className="signin-form">
            <label htmlFor="email">Administrator email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
            />
            <button className="button button-dark" disabled={busy}>
              {busy ? "Sending…" : "Send sign-in link"}
            </button>
          </form>
          {message ? <p className="form-message" role="status">{message}</p> : null}
        </section>
      </main>
    </PublicLayout>
  );
}

function AuthCallbackPage({ auth }) {
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const error = hash.get("error_description");

  useEffect(() => {
    if (!auth.loading && auth.session) {
      window.history.replaceState({}, "", "/admin");
      navigate("/admin", { replace: true });
    }
  }, [auth.loading, auth.session]);

  return (
    <PublicLayout>
      <main className="narrow-page">
        <section className="panel status-panel" aria-live="polite">
          <p className="eyebrow dark">Administrator access</p>
          <h1>{error ? "Sign-in link unavailable" : "Completing sign in"}</h1>
          <p>{error || "Verifying your secure link. This should take only a moment."}</p>
          {error ? <InternalLink className="text-link" href="/signin">Request another link</InternalLink> : null}
        </section>
      </main>
    </PublicLayout>
  );
}

function AdminPage({ auth }) {
  const [authorization, setAuthorization] = useState({ loading: true, allowed: false, message: "" });
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState("");

  useEffect(() => {
    if (auth.loading) return;
    if (!auth.session) {
      navigate("/signin", { replace: true });
      return;
    }

    let active = true;
    async function authorize() {
      if (import.meta.env.DEV) {
        const email = auth.session.user.email?.toLowerCase() || "";
        if (active) setAuthorization({ loading: false, allowed: ADMIN_EMAILS.has(email), message: "" });
        return;
      }

      try {
        const response = await fetch("/api/admin/session", {
          headers: { Authorization: `Bearer ${auth.session.access_token}` }
        });
        const payload = await response.json();
        if (!active) return;
        setAuthorization({
          loading: false,
          allowed: response.ok && payload.authorized === true,
          message: response.ok ? "" : payload.error || "Administrator access could not be verified."
        });
      } catch {
        if (active) setAuthorization({ loading: false, allowed: false, message: "Administrator access could not be verified." });
      }
    }

    authorize();
    return () => { active = false; };
  }, [auth.loading, auth.session]);

  const filteredGuides = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return guideCatalog.guides.slice(0, 12);
    return guideCatalog.guides.filter((guide) =>
      [guide.city, guide.country, guide.airportCode, guide.slug].some((value) => value?.toLowerCase().includes(query))
    ).slice(0, 24);
  }, [search]);

  async function signOut() {
    if (supabase) await supabase.auth.signOut();
    navigate("/signin", { replace: true });
  }

  async function copyLink(slug) {
    const link = `${APP_ORIGIN}/guides/${slug}`;
    await navigator.clipboard.writeText(link);
    setCopied(slug);
    window.setTimeout(() => setCopied(""), 1800);
  }

  if (auth.loading || authorization.loading) {
    return <AdminStatus title="Checking administrator access" text="One moment." />;
  }

  if (!authorization.allowed) {
    return (
      <AdminStatus
        title="Access unavailable"
        text={authorization.message || "This account is not authorized for The Layover app administration."}
        action={<button className="button button-dark" onClick={signOut}>Sign out</button>}
      />
    );
  }

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <InternalLink className="brand" href="/">
          <img src={LOGO_URL} alt="" width="48" height="31" />
          <span className="brand-copy">The Layover<small>App administration</small></span>
        </InternalLink>
        <div className="admin-user">
          <span>{auth.session.user.email}</span>
          <button onClick={signOut}>Sign out</button>
        </div>
      </header>
      <main className="admin-main">
        <section className="admin-intro">
          <p className="eyebrow dark">Preparatory phase</p>
          <h1>App foundation</h1>
          <p>A deliberately small control surface for the native app groundwork.</p>
        </section>

        <section className="status-grid" aria-label="Integration readiness">
          <StatusCard label="Published guide links" value={String(guideCatalog.guides.length)} state="Ready" ready />
          <StatusCard label="Supabase authentication" value="Admin only" state="Ready" ready />
          <StatusCard label="iOS association" value="Identifiers needed" state="Deferred" />
          <StatusCard label="Android association" value="Identifiers needed" state="Deferred" />
          <StatusCard label="RevenueCat" value="Account not created" state="Deferred" />
          <StatusCard label="AppsFlyer" value="Account not created" state="Deferred" />
        </section>

        <section className="admin-section" aria-labelledby="link-builder-title">
          <div className="admin-section-heading">
            <div>
              <p className="eyebrow dark">Universal-link foundation</p>
              <h2 id="link-builder-title">Guide links</h2>
            </div>
            <label className="search-field">
              <span>Search guides</span>
              <input
                type="search"
                placeholder="Tokyo, TYO, Japan…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
          </div>
          <div className="guide-table" role="table" aria-label="App guide links">
            {filteredGuides.length ? filteredGuides.map((guide) => (
              <div className="guide-row" role="row" key={guide.slug}>
                <div className="airport-code" role="cell">{guide.airportCode}</div>
                <div className="guide-name" role="cell">
                  <strong>{guide.city}</strong>
                  <span>{guide.country}</span>
                </div>
                <code role="cell">/guides/{guide.slug}</code>
                <div className="row-actions" role="cell">
                  <button onClick={() => copyLink(guide.slug)}>{copied === guide.slug ? "Copied" : "Copy"}</button>
                  <a href={`/guides/${guide.slug}`} target="_blank" rel="noreferrer">Preview</a>
                </div>
              </div>
            )) : <p className="empty-state">No published guide links match that search.</p>}
          </div>
          {!search && guideCatalog.guides.length > filteredGuides.length ? (
            <p className="table-note">Showing the first 12 links. Search to find any published city.</p>
          ) : null}
        </section>
      </main>
    </div>
  );
}

function AdminStatus({ title, text, action }) {
  return (
    <PublicLayout>
      <main className="narrow-page">
        <section className="panel status-panel">
          <p className="eyebrow dark">Private prototype</p>
          <h1>{title}</h1>
          <p>{text}</p>
          {action}
        </section>
      </main>
    </PublicLayout>
  );
}

function StatusCard({ label, value, state, ready = false }) {
  return (
    <article className="status-card">
      <div className={`status-dot ${ready ? "ready" : "deferred"}`} aria-hidden="true" />
      <p>{label}</p>
      <strong>{value}</strong>
      <span>{state}</span>
    </article>
  );
}

function GuideDirectoryPage() {
  const [search, setSearch] = useState("");
  const filteredGuides = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return guideCatalog.guides;
    return guideCatalog.guides.filter((guide) =>
      [guide.city, guide.country, guide.airportCode].some((value) => value?.toLowerCase().includes(query))
    );
  }, [search]);

  return (
    <PublicLayout>
      <main className="content-page">
        <section className="page-intro">
          <p className="eyebrow dark">App link directory</p>
          <h1>Future routes. Useful today.</h1>
          <p>
            These links will open city guides inside the native apps. Until then, each one provides a clean path to the existing Layover guide.
          </p>
          <label className="large-search">
            <span>Search city, country, or airport code</span>
            <input
              type="search"
              placeholder="Search Tokyo, Japan, TYO…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
        </section>
        <section className="guide-grid" aria-live="polite">
          {filteredGuides.map((guide) => (
            <InternalLink className="guide-card" href={`/guides/${guide.slug}`} key={guide.slug}>
              <span className="airport-code">{guide.airportCode}</span>
              <h2>{guide.city}</h2>
              <p>{guide.state ? `${guide.state} · ` : ""}{guide.country}</p>
              <span className="card-action">Open app link</span>
            </InternalLink>
          ))}
          {!filteredGuides.length ? <p className="empty-state">No published guide links match that search.</p> : null}
        </section>
      </main>
    </PublicLayout>
  );
}

function GuideLinkPage({ slug }) {
  const guide = guideCatalog.guides.find((item) => item.slug === slug);
  if (!guide) return <NotFoundPage />;

  return (
    <PublicLayout>
      <main className="guide-fallback">
        <section className="guide-ticket">
          <div className="ticket-code">{guide.airportCode}</div>
          <div className="ticket-copy">
            <p className="eyebrow dark">The Layover app link</p>
            <h1>{guide.city}</h1>
            <p className="guide-location">{guide.state ? `${guide.state} · ` : ""}{guide.country}</p>
            <p>{guide.description}</p>
            <div className="button-row left">
              <a className="button button-dark" href={guide.ghostUrl}>View current city guide</a>
              <InternalLink className="button button-outline" href="/guides">All app links</InternalLink>
            </div>
            <p className="route-note">
              Reserved native route: <code>{APP_ORIGIN}/guides/{guide.slug}</code>
            </p>
          </div>
        </section>
      </main>
    </PublicLayout>
  );
}

function PolicyPage({ type }) {
  const privacy = type === "privacy";
  return (
    <PublicLayout>
      <main className="narrow-page">
        <article className="panel prose-panel">
          <p className="eyebrow dark">App prototype</p>
          <h1>{privacy ? "Privacy" : "Terms"}</h1>
          <p>
            This preparatory app surface is operated separately from the Ghost member session at layover.tips. It currently supports authorized administrator sign-in and public guide-link fallbacks only.
          </p>
          <p>
            The Layover&apos;s current {privacy ? "privacy policy" : "terms"} remains the canonical policy for the live publication, newsletters, memberships, and city guides.
          </p>
          <a className="text-link" href={`https://layover.tips/${privacy ? "privacy" : "terms"}/`}>
            Read the current {privacy ? "privacy policy" : "terms"} at layover.tips
          </a>
        </article>
      </main>
    </PublicLayout>
  );
}

function SupportPage() {
  return (
    <PublicLayout>
      <main className="narrow-page">
        <article className="panel prose-panel">
          <p className="eyebrow dark">App prototype</p>
          <h1>Support</h1>
          <p>For questions about this app prototype or administrator access, email support@layover.tips.</p>
          <a className="text-link" href="mailto:support@layover.tips">Email support@layover.tips</a>
        </article>
      </main>
    </PublicLayout>
  );
}

function DeleteAccountPage() {
  return (
    <PublicLayout>
      <main className="narrow-page">
        <article className="panel prose-panel">
          <p className="eyebrow dark">App prototype</p>
          <h1>Delete app account</h1>
          <p>
            Email support@layover.tips from the address used for app sign-in. Deleting an app-prototype account does not automatically remove a separate Ghost newsletter membership.
          </p>
          <a className="text-link" href="mailto:support@layover.tips?subject=Delete%20my%20Layover%20app%20account">Request account deletion</a>
        </article>
      </main>
    </PublicLayout>
  );
}

function NotFoundPage() {
  return (
    <PublicLayout>
      <main className="narrow-page">
        <section className="panel status-panel">
          <p className="eyebrow dark">App prototype</p>
          <h1>Route not found</h1>
          <p>This app route has not been defined.</p>
          <InternalLink className="text-link" href="/">Return to the app preview</InternalLink>
        </section>
      </main>
    </PublicLayout>
  );
}

createRoot(document.getElementById("root")).render(<App />);
